import React, { useCallback, useMemo, useState } from 'react';
import { Form, Button, Select, Tooltip, Modal } from 'antd';
import {
  LockOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { isString } from 'lodash';
import {
  BulkEditOperationFunc,
  BulkPermissionsAccountParam,
  LoginUserProfile,
  PermissionDescriptor,
  UserProfileInfo,
} from '../../types';
import { useGetUserMeta } from '@src/components/user-management/hooks';
import { useFeature } from '@src/globalStore/featureStore';
import { getUserProfile } from '@src/store/common/selectors';
import { Loading } from '@src/components/common';
import featureNames from '@src/utils/featureToggle/constants';
import { MAX_USER_COUNT_TO_OPERATE } from '../utils';
import { filterPermissionForDisabledFlag } from '../UserProfileEditor/contextCenterEligibility';
import { OperationStatusInfo, PERMISSION_NO_GRANT_TOOLTIP } from '../UserProfileEditor/utils';
import { NexaPermissionTooltipContent } from '../UserProfileEditor/tooltipContent';
import {
  ActionResponseTitle,
  IconWrapper,
  StatusContainer,
  StatusContent,
  StatusTitle,
  StyledForm,
} from '../UserProfileEditor/styled';
import {
  bulkResultToAccountStatusInfo,
  getLegacyPermissionDescriptors,
  hasNotAMemberSkip,
  LegacyPermissionDescriptor,
  maxAccountsForUserCount,
  pluralize,
  toBulkSetValue,
  validateBulkAccounts,
} from './utils';

interface Props {
  /** the multi-selected users to apply the permission to (map ticket T8/T9) */
  usersToBulkEdit: UserProfileInfo[];
  onBulkEdit: BulkEditOperationFunc;
  onCancel: () => void;
}

interface FormValues {
  permissionKey?: string;
  /** boolean permissions carry 'true'/'false'; enum permissions carry the option value */
  value?: string;
  accounts: number[];
}

/** A descriptor in the picker: an active/registry one from the server, or a client legacy one. */
type PickerDescriptor = PermissionDescriptor | LegacyPermissionDescriptor;

/** One <Select> option for the value picker — Yes/No for booleans, def.options for enums. */
function valueOptions(def?: PickerDescriptor): { value: string; label: string }[] {
  if (!def) {
    return [];
  }
  if (def.kind === 'enum') {
    return (def.options ?? []).map((o) => ({ value: o.value, label: o.label }));
  }
  // boolean → a Yes/No picker (stored as the canonical 'true'/'false' strings the server coerces).
  return [
    { value: 'true', label: 'Yes' },
    { value: 'false', label: 'No' },
  ];
}

/**
 * One-permission-per-operation bulk editor (map ticket T8/T9). The operator picks ONE permission, a
 * value, and the C3 accounts to apply it in; it's applied to the selected users across those
 * accounts. Accounts where the operator can't grant the chosen permission (per the server's
 * `canGrantByAccount` matrix) are disabled in the selector. Replaces the old full-override bulk form
 * and the separate By-Permission tab.
 */
export const BulkEditPermissions = ({ usersToBulkEdit, onBulkEdit, onCancel }: Props) => {
  const operatorProfile = useSelector<any, LoginUserProfile>(getUserProfile);
  const {
    isFetching,
    data: { accountList = [], permissionDefinitions = [], canGrantByAccount = {} } = {},
  } = useGetUserMeta();

  const [form] = Form.useForm<FormValues>();
  const selectedPermissionKey = Form.useWatch('permissionKey', form);
  const selectedAccounts = Form.useWatch('accounts', form);
  const [requesting, setRequesting] = useState(false);
  const [actionResponse, setActionResponse] = useState<OperationStatusInfo[]>([]);
  const [showNotAMemberHint, setShowNotAMemberHint] = useState(false);
  // The permission + value that were applied, captured at apply time so the result header can name
  // what changed (the form may be reset by then).
  const [appliedSummary, setAppliedSummary] = useState<string>('');

  // Include the operator's own account so single-account operators still see a target (mirrors the
  // invite editor's mergedAccountList).
  const mergedAccountList = useMemo(() => {
    if (operatorProfile) {
      const { customer_id, customer_name } = operatorProfile;
      if (!accountList.find((it) => it.id === customer_id)) {
        return [...accountList, { id: customer_id, name: customer_name }];
      }
    }
    return accountList;
  }, [accountList, operatorProfile]);

  // Picker offers BOTH the active/registry (KV) descriptors from the server AND the legacy
  // (column-storage) permissions the old full-form editor could set. Legacy perms aren't in the
  // server descriptor list (seeded inactive by T1), so they're client-defined here.
  // Context Center is omitted unless nexa-context-center is on for this operator.
  const contextCenterEnabled = useFeature(featureNames.NEXA_CONTEXT_CENTER);
  const mergedDescriptors = useMemo<PickerDescriptor[]>(
    () =>
      filterPermissionForDisabledFlag(
        [...permissionDefinitions, ...getLegacyPermissionDescriptors()],
        contextCenterEnabled,
      ),
    [permissionDefinitions, contextCenterEnabled],
  );
  const selectedDef = useMemo(
    () => mergedDescriptors.find((d) => d.key === selectedPermissionKey),
    [mergedDescriptors, selectedPermissionKey],
  );

  // id -> email for the selected users, so the result report can show emails even for the
  // "not a member" skip (where the server can't resolve a profile/email in that account).
  const emailByUserId = useMemo(() => {
    const m = new Map<number, string>();
    for (const u of usersToBulkEdit) {
      m.set(u.userId, u.email);
    }
    return m;
  }, [usersToBulkEdit]);

  /** Whether the operator can grant the currently-chosen permission in `accountId`. */
  const canGrantIn = useCallback(
    (accountId: number): boolean => {
      if (!selectedPermissionKey) {
        return true;
      }
      const perAccount = canGrantByAccount[accountId];
      // Missing account/key ⇒ permissive fallback (same convention as canGrant).
      if (!perAccount || perAccount[selectedPermissionKey] === undefined) {
        return true;
      }
      return perAccount[selectedPermissionKey];
    },
    [canGrantByAccount, selectedPermissionKey],
  );

  // Grantability for a specific permission key in an account (used for the pre-select/prune below,
  // reading the just-selected key rather than the watched state which can lag onChange).
  const canGrantKeyIn = useCallback(
    (permKey: string, accountId: number): boolean => {
      const perAccount = canGrantByAccount[accountId];
      if (!permKey || !perAccount || perAccount[permKey] === undefined) {
        return true; // permissive fallback (same convention as canGrant)
      }
      return perAccount[permKey];
    },
    [canGrantByAccount],
  );

  // When the chosen permission changes: (1) drop any now-ungrantable accounts so the operator can't
  // submit accounts the server would reject, and (2) pre-select the operator's OWN account if they
  // can grant this permission there (the common case — editing users in your current account),
  // preserving any other accounts already chosen.
  const handlePermissionChange = useCallback(
    (permKey: string) => {
      const current: number[] = form.getFieldValue('accounts') ?? [];
      let next = current.filter((id) => canGrantKeyIn(permKey, id));
      const ownAccountId = operatorProfile?.customer_id;
      if (
        ownAccountId !== undefined &&
        !next.includes(ownAccountId) &&
        canGrantKeyIn(permKey, ownAccountId)
      ) {
        next = [...next, ownAccountId];
      }
      form.setFieldValue('accounts', next);
      // reset the value when switching permission kinds
      form.setFieldValue('value', undefined);
    },
    [form, canGrantKeyIn, operatorProfile],
  );

  const onFinish = useCallback(
    async (raw: unknown) => {
      const { permissionKey, value, accounts } = raw as FormValues;
      if (!permissionKey || value === undefined || !accounts?.length || !selectedDef) {
        return;
      }
      const bulkValueKind = (selectedDef as LegacyPermissionDescriptor).bulkValueKind;
      const setValue = toBulkSetValue(bulkValueKind, selectedDef.kind === 'enum', value);
      const userIds = usersToBulkEdit.map((it) => it.userId);
      const payload: BulkPermissionsAccountParam[] = accounts.map((id) => ({
        customer: { id },
        set: { [permissionKey]: setValue },
        userIds,
      }));

      const valueLabel =
        valueOptions(selectedDef).find((o) => o.value === value)?.label ?? String(value);
      Modal.confirm({
        title: 'Apply permission change',
        content: `Set "${selectedDef.label}" to "${valueLabel}" for ${usersToBulkEdit.length} user${
          usersToBulkEdit.length === 1 ? '' : 's'
        } across ${accounts.length} account${accounts.length === 1 ? '' : 's'}?`,
        okText: 'Apply',
        onOk: async () => {
          try {
            setRequesting(true);
            const result = await onBulkEdit(payload);
            if (result) {
              setActionResponse(bulkResultToAccountStatusInfo(result, emailByUserId, userIds));
              setShowNotAMemberHint(hasNotAMemberSkip(result));
              setAppliedSummary(`${selectedDef.label} → ${valueLabel}`);
            }
          } finally {
            setRequesting(false);
          }
        },
      });
    },
    [onBulkEdit, selectedDef, usersToBulkEdit, emailByUserId],
  );

  // Return to the empty form with the SAME selected users, so the operator can apply another
  // permission without re-selecting. Clears the result + the form fields; keeps usersToBulkEdit.
  const handleEditAnother = useCallback(() => {
    form.resetFields();
    setActionResponse([]);
    setShowNotAMemberHint(false);
    setAppliedSummary('');
  }, [form]);

  // The server caps a By-User op on total profiles (users × accounts) at MAX_USER_COUNT_TO_OPERATE,
  // so the real ceiling on accounts depends on how many users are selected. Surface that instead of
  // the flat "Max 50" (which is only the account-count cap, not the effective limit).
  const maxAccounts = useMemo(
    () => maxAccountsForUserCount(usersToBulkEdit.length),
    [usersToBulkEdit.length],
  );

  // Mirror the field validator's verdict so the submit button can visually disable when the current
  // selection is over-cap (a failing validator alone blocks submit but leaves the button looking
  // enabled). Only meaningful once an account is chosen; empty selection is caught by `required`.
  const accountsValidation = useMemo(
    () =>
      validateBulkAccounts({
        accountCount: Array.isArray(selectedAccounts) ? selectedAccounts.length : 0,
        userCount: usersToBulkEdit.length,
      }),
    [selectedAccounts, usersToBulkEdit.length],
  );
  const hasAccountsSelected = Array.isArray(selectedAccounts) && selectedAccounts.length > 0;

  const isActionDone = actionResponse.length > 0;

  if (isFetching) {
    return <Loading />;
  }

  return (
    <div>
      <div style={{ maxWidth: '800px', margin: '20px auto' }}>
        {isActionDone && (
          <div>
            <ActionResponseTitle key="summary">
              Permission update result{appliedSummary ? `: ${appliedSummary}` : ':'}
            </ActionResponseTitle>
            {actionResponse.map((it) => (
              <OperationStatus
                key={it.state + it.title + it.content}
                state={it.state}
                title={it.title}
                content={it.content}
              />
            ))}
            {showNotAMemberHint && (
              <div
                style={{
                  marginTop: '12px',
                  padding: '10px 12px',
                  background: '#f0f5ff',
                  border: '1px solid #adc6ff',
                  borderRadius: '6px',
                  fontSize: '13px',
                  color: 'rgba(0, 0, 0, 0.65)',
                }}
              >
                <InfoCircleOutlined style={{ color: '#1890ff', marginRight: '8px' }} />
                Skipped users don&apos;t have a profile in that account. If they should have access
                there, invite them to the account first, then re-apply the permission.
              </div>
            )}
          </div>
        )}
        {!isActionDone && (
          <StyledForm form={form as any} layout="vertical" onFinish={onFinish}>
            <div
              style={{
                fontSize: '18px',
                marginBottom: '14px',
                fontWeight: 600,
                color: 'rgba(0, 0, 0, 0.88)',
              }}
            >
              Edit Permissions for {usersToBulkEdit.length} selected user
              {usersToBulkEdit.length === 1 ? '' : 's'}
              <div
                style={{
                  fontSize: '13px',
                  fontWeight: 400,
                  color: 'rgba(0, 0, 0, 0.55)',
                  marginTop: '4px',
                }}
              >
                Set one permission to one value, applied to the selected users in the chosen
                accounts. Accounts where you can&apos;t grant the permission are disabled.
              </div>
              <div
                style={{
                  fontSize: '13px',
                  fontWeight: 400,
                  color: 'rgba(0, 0, 0, 0.65)',
                  marginTop: '8px',
                  maxHeight: '84px',
                  overflowY: 'auto',
                  lineHeight: '1.6',
                }}
              >
                {usersToBulkEdit.map((u) => (
                  <span key={u.userId} style={{ display: 'inline-block', marginRight: '14px' }}>
                    <span style={{ marginRight: '6px', color: 'rgba(0, 0, 0, 0.35)' }}>&bull;</span>
                    {u.email}
                  </span>
                ))}
              </div>
            </div>

            <Form.Item
              label="Permission"
              name="permissionKey"
              rules={[{ required: true, message: 'Please select a permission' }]}
            >
              <Select
                placeholder="select a permission"
                onChange={handlePermissionChange}
                options={mergedDescriptors.map((def) => ({
                  value: def.key,
                  label: def.label,
                }))}
              />
            </Form.Item>

            <Form.Item
              label={
                <span>
                  Set value
                  {(selectedDef as LegacyPermissionDescriptor)?.bulkValueKind === 'nexa' && (
                    <Tooltip
                      title={
                        <NexaPermissionTooltipContent
                          showNexaEnabledOption={operatorProfile?.can_manage_nexa}
                        />
                      }
                    >
                      <InfoCircleOutlined
                        style={{ marginLeft: '6px', cursor: 'pointer', color: '#1890ff' }}
                      />
                    </Tooltip>
                  )}
                </span>
              }
              name="value"
              rules={[{ required: true, message: 'Please select a value' }]}
            >
              <Select
                placeholder={selectedDef ? 'select a value' : 'select a permission first'}
                disabled={!selectedDef}
                options={valueOptions(selectedDef)}
              />
            </Form.Item>

            <Form.Item
              label={`Accounts (max ${maxAccounts} for ${usersToBulkEdit.length} selected ${pluralize(
                usersToBulkEdit.length,
                'user',
                'users',
              )})`}
              name="accounts"
              rules={[
                {
                  required: true,
                  // Enforce BOTH the account-count cap and the total (users × accounts) cap the
                  // server actually applies, so the operator sees the real limit before submitting.
                  validator: (_, value) => {
                    const result = validateBulkAccounts({
                      accountCount: Array.isArray(value) ? value.length : 0,
                      userCount: usersToBulkEdit.length,
                    });
                    return result.ok ? Promise.resolve() : Promise.reject(result.message);
                  },
                },
              ]}
            >
              <Select
                mode="multiple"
                // Keep selected-account tags on one responsive line so a large selection doesn't
                // push the Apply buttons out of view.
                maxTagCount="responsive"
                // Grantability (and pre-select) depend on the chosen permission, so the account
                // picker only makes sense once a permission is selected. Also disabled when the user
                // selection alone exceeds the profile cap (maxAccounts === 0): no account count is
                // valid, so block selection outright rather than let the operator submit a doomed op.
                disabled={!selectedDef || maxAccounts === 0}
                placeholder={
                  !selectedDef
                    ? 'select a permission first'
                    : maxAccounts === 0
                      ? `too many users selected (limit ${MAX_USER_COUNT_TO_OPERATE})`
                      : 'select account'
                }
                filterOption={(input, option) =>
                  isString(option?.label)
                    ? option.label.toLowerCase().includes(input.toLowerCase())
                    : false
                }
                options={[
                  {
                    disabled: true,
                    label: (
                      <div style={{ display: 'flex', gap: '10px' }}>
                        <Button
                          size="small"
                          onClick={() =>
                            form.setFieldValue(
                              'accounts',
                              // Select All respects grantability: only add accounts the operator can
                              // grant the chosen permission in.
                              mergedAccountList
                                .filter((it) => canGrantIn(it.id))
                                .map((it) => it.id),
                            )
                          }
                        >
                          Select All
                        </Button>
                        <Button size="small" onClick={() => form.setFieldValue('accounts', [])}>
                          Clear All
                        </Button>
                      </div>
                    ),
                  },
                  ...mergedAccountList.map((it) => {
                    const grantable = canGrantIn(it.id);
                    return {
                      value: it.id,
                      disabled: !grantable,
                      label: grantable ? (
                        it.name
                      ) : (
                        <Tooltip
                          title={
                            selectedDef
                              ? `You can't grant "${selectedDef.label}" in ${it.name}.`
                              : PERMISSION_NO_GRANT_TOOLTIP
                          }
                        >
                          <span style={{ color: 'rgba(0,0,0,0.35)' }}>
                            <LockOutlined style={{ marginRight: 6 }} />
                            {it.name}
                          </span>
                        </Tooltip>
                      ),
                    };
                  }),
                ]}
                allowClear
              />
            </Form.Item>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <Button onClick={() => onCancel()}>Back</Button>
              <Tooltip
                title={
                  hasAccountsSelected && !accountsValidation.ok ? accountsValidation.message : ''
                }
              >
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={requesting}
                  // Reflect the over-cap state visually — a failing validator blocks submit but leaves
                  // the button looking enabled. Empty selection stays enabled so `required` surfaces
                  // its own message on click.
                  disabled={hasAccountsSelected && !accountsValidation.ok}
                >
                  Review &amp; apply
                </Button>
              </Tooltip>
            </div>
          </StyledForm>
        )}
        {isActionDone && (
          <div
            style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '16px' }}
          >
            <Button onClick={() => onCancel()}>Back to users</Button>
            <Button type="primary" onClick={handleEditAnother}>
              Edit another permission
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

const OperationStatus = ({ state, title, content }: OperationStatusInfo) => (
  <StatusContainer>
    <IconWrapper>
      {state === 'success' ? (
        <CheckCircleOutlined style={{ color: 'rgb(34 197 94' }} />
      ) : (
        <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
      )}
    </IconWrapper>
    <div>
      <StatusTitle>{title}</StatusTitle>
      <StatusContent>{content}</StatusContent>
    </div>
  </StatusContainer>
);
